import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { Card, Chip, DataRow } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  BankIcon,
  CheckmarkCircle02Icon,
  CreditCardIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { useNavigate } from "react-router-dom";
import { titleCase } from "title-case";
import { GET_AUTO_BALANCE_RULES } from "graphql/company";
import AccountType from "enums/AccountType";
import ApplicationType from "enums/ApplicationType";
import {
  useResolvedBankAccounts,
  UseGetBankAccountsResult,
  BankAccount,
} from "hooks/useGetBankAccounts";
import { Inline, Stack } from "components/layout/reusableLayouts";
import CustomSkeleton from "components/common/CustomSkeleton";
import AccountActionsMenu, {
  AccountDrawerRequest,
} from "components/admin/bank/AccountActionsMenu";
import AccountRowActions from "components/admin/bank/AccountRowActions";
import RecentTransactions from "components/common/transactions/RecentTransactions";
import TransactionDetailsDrawer from "components/common/transactions/TransactionDetailsDrawer";

interface AccountsPanelProps {
  // Provided by the dashboard (single shared fetch); self-fetches when absent.
  bankAccounts?: UseGetBankAccountsResult;
}

const accountLabel = (account: BankAccount, fallback: string): string => {
  const name = account.nickname || account.bankName;
  return name ? titleCase(name.toLowerCase()) : fallback;
};

/**
 * The company's own accounts, the actions that move money through them, and the
 * transactions that just did.
 *
 * One panel for both programs. A charge account and a prepaid one differ in
 * what their row's value means (credit available against cash held) and in
 * which actions the overflow menu offers, which is not enough to justify the
 * two near-identical panels this replaced.
 */
const AccountsPanel: React.FC<AccountsPanelProps> = ({ bankAccounts }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [transactionId, setTransactionId] = useState("");
  const [requestedDrawer, setRequestedDrawer] =
    useState<AccountDrawerRequest | null>(null);

  const {
    internalAccounts,
    isLoading: loadingAccounts,
    refetchAllAccounts,
  } = useResolvedBankAccounts(bankAccounts, {
    accountTypes: [AccountType.FINANCIAL_ACCOUNT],
  });

  const { data: rulesData, refetch: refetchBalanceSettings } = useQuery(
    GET_AUTO_BALANCE_RULES,
    { fetchPolicy: "network-only" },
  );

  // Each program keeps its own rule, so each row says whether its own is on.
  const autoBalanceOn = (accountId: string) =>
    (
      (rulesData?.getAutoBalanceRules ?? []) as {
        toAccountId: string;
        isDisabled?: boolean;
      }[]
    ).some((rule) => rule.toAccountId === accountId && !rule.isDisabled);

  // One row is one card program, and a company can hold a prepaid one beside a
  // charge one, so what a row offers is decided per row.
  const singleAccount = internalAccounts.length <= 1;
  const headerAccount = internalAccounts[0];

  const refreshBankAndBalance = () => {
    refetchAllAccounts();
    refetchBalanceSettings();
  };

  return (
    <Card
      variant="outlined"
      title={t("Accounts")}
      action={{
        type: "content",
        // One owner for the drawers however many rows list them.
        content: (
          <AccountActionsMenu
            account={headerAccount}
            hideTrigger={!singleAccount}
            requested={requestedDrawer}
            onRequestedClose={() => setRequestedDrawer(null)}
            refreshTransactions={refreshBankAndBalance}
            bankAccounts={bankAccounts}
          />
        ),
      }}
      sx={{ height: "100%" }}
    >
      <Stack gap="var(--space-4)">
        {loadingAccounts && internalAccounts.length === 0 ? (
          <CustomSkeleton variant="list" rows={2} hideCircular />
        ) : (
          internalAccounts.map((account) => (
            // The menu follows the amount, which DataRow's own trailing slot cannot do.
            <Inline key={account.id} gap="var(--space-2)" wrap={false}>
              <DataRow
                sx={{ flex: 1, minWidth: 0 }}
                type="amount"
                icon={{
                  glyph: (
                    <HugeiconsIcon
                      icon={
                        account.applicationType === ApplicationType.CHARGE
                          ? CreditCardIcon
                          : BankIcon
                      }
                      size={18}
                    />
                  ),
                  tone: "brand",
                }}
                title={accountLabel(account, t("Account"))}
                tag={
                  autoBalanceOn(account.id) ? (
                    <Chip
                      size="sm"
                      tone="neutral"
                      leading={
                        <HugeiconsIcon icon={CheckmarkCircle02Icon} size={14} />
                      }
                      label={t("Auto balance")}
                    />
                  ) : undefined
                }
                subtitle={account.last4 ? `•••• ${account.last4}` : undefined}
                amount={account.availableAmount}
                // A row with a menu of its own is not itself clickable, so viewing
                // its activity becomes a menu item.
                onClick={
                  singleAccount
                    ? () =>
                        navigate("/admin/transactions", {
                          state: { accountId: account.id },
                        })
                    : undefined
                }
              />
              {!singleAccount && (
                <AccountRowActions
                  account={account}
                  onRequestDrawer={setRequestedDrawer}
                />
              )}
            </Inline>
          ))
        )}
      </Stack>

      <Divider />

      <Stack gap="var(--space-3)" sx={{ flex: 1, minHeight: 0 }}>
        <Typography
          variant="label"
          component="h4"
          sx={{ color: "var(--color-text-tertiary)" }}
        >
          {t("Recent transactions")}
        </Typography>
        <RecentTransactions scope="company" onRowClick={setTransactionId} />
      </Stack>

      <TransactionDetailsDrawer
        open={!!transactionId}
        handleCloseDrawer={() => setTransactionId("")}
        transactionId={transactionId}
      />
    </Card>
  );
};

export default AccountsPanel;
